import { useState, useEffect, useCallback } from 'react';
import { useLang } from '../../../hooks/useLang';

/**
 * BackToTop — floating back-to-top button + top reading progress bar.
 * - Progress bar: thin line at the very top of the viewport, tracks page scroll %.
 * - Button: appears after scrolling 400px, smooth-scrolls to top on click.
 */
export default function BackToTop() {
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const { t } = useLang();

  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    const pct = docHeight > 0 ? Math.min(100, (scrollTop / docHeight) * 100) : 0;
    setProgress(pct);
    setVisible(scrollTop > 400);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      {/* ── Top Reading Progress Bar ── */}
      <div
        className="fixed top-0 left-0 right-0 z-[9999] h-[3px] pointer-events-none"
        style={{ background: 'rgba(0,0,0,0.06)' }}
      >
        <div
          className="h-full transition-all duration-100"
          style={{
            width: `${progress}%`,
            background: 'linear-gradient(90deg, #A78BFA 0%, #F59E0B 50%, #10B981 100%)',
          }}
        />
      </div>

      {/* ── Back to Top Button ── */}
      <button
        onClick={scrollToTop}
        aria-label={t('返回顶部', 'Back to top')}
        className="fixed bottom-24 right-5 z-50 flex flex-col items-center justify-center gap-0.5 cursor-pointer transition-all duration-300 whitespace-nowrap"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0) scale(1)' : 'translateY(16px) scale(0.85)',
          pointerEvents: visible ? 'auto' : 'none',
        }}
      >
        {/* Circular button */}
        <div
          className="w-11 h-11 flex items-center justify-center rounded-full"
          style={{
            background: 'linear-gradient(135deg, #A78BFA, #7C3AED)',
            boxShadow: '0 4px 16px rgba(124,58,237,0.35)',
          }}
        >
          <i className="ri-arrow-up-line text-white text-lg"></i>
        </div>
        {/* Progress ring label */}
        <span
          className="text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full"
          style={{ background: 'rgba(124,58,237,0.85)', minWidth: '32px', textAlign: 'center' }}
        >
          {Math.round(progress)}%
        </span>
      </button>
    </>
  );
}
